import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import * as Yup from "yup";
import { Options } from "../../../../../../store/types";
import { useAppSelector } from "../../../../../../store/hooks";

import {
  addItemsToCart,
  getAmcMasters,
  getInvestorDetailsByPan,
  getKycDetails,
  getNewPurchaseSchemes,
} from "../../../../../../store/actions";
import {
  getNewPurchaseCategory,
  validateSubBroker,
} from "../../../../../../store/actions/cart";
import { convertToInr, handleError } from "../../../../../../utils/cart";
import { FormikHelpers } from "formik";
import { mobileRegex } from "../..";
import { TextFieldProps } from "@material-ui/core";

const FAMILY_OPTIONS = [
  {
    label: "Self",
    value: "SE",
  },
  {
    label: "Spouse",
    value: "SP",
  },
  {
    label: "Dependent Children",
    value: "DC",
  },
  {
    label: "Dependent Siblings",
    value: "DS",
  },
  {
    label: "Dependent Parents",
    value: "DP",
  },
  {
    label: "Guardian",
    value: "GD",
  },
];

interface SchemeOption {
  label: string;
  value: string;
  minAmount: number;
  multiples: number;
  scheme: string;
  plan: string;
  option: string;
  combo_flag: string;
  combo_disclaimer: string;
}

const FIELD_NAMES: Array<keyof Values> = [
  "amc",
  "category",
  "scheme",
  "amount",
];

export default function useNewPurchase() {
  const dispatch = useDispatch();
  const history = useHistory();

  const [categoryOptions, setCategoryOptions] = useState<Options>([]);
  const [funds, setFunds] = useState<Options>([]);

  const [schemeOptions, setSchemeOptions] = useState<Array<SchemeOption>>([]);
  const [euinOptions, setEuinOptions] = useState<Options>([]);
  const [modalDetails, setModalDetails] = useState({
    isOpen: false,
    bulkOrderId: "",
    ihNo: "",
  });

  const { fundDetails, arnCode, userId, investorData } = useAppSelector(
    ({ cartReducer, authReducer }) => ({
      fundDetails: cartReducer.fundDetails,
      arnCode: authReducer.token,
      userId: authReducer.userId,
      investorData: authReducer.investorData,
    })
  );

  const investorFundDetails = fundDetails.filter(
    ({ PAN }) => PAN === investorData.PAN
  );

  const email = investorFundDetails.find(({ email }) => email)?.email;
  const mobile = investorFundDetails.find(({ Mobile }) => Mobile)?.Mobile;

  const [initialValues, setInitialValues] = useState<Values>({
    email,
    invCategory: "11",
    mobile,
    amc: "",
    amount: "",
    category: "",
    scheme: "",
    emailFamily: "",
    mobileFamily: "",
    euinDeclaration: "Yes",
    euin: "",
    subArn: "",
    subBrokerCode: "",
    arnCode,
    combo: false,
  });

  const resetLaterFields = useCallback(function userId(
    values: Values,
    key: keyof Values,
    fieldValues: Partial<Values>
  ): Values {
    const index = FIELD_NAMES.findIndex((fieldName) => fieldName === key);
    const updatedValues = FIELD_NAMES.slice(index).reduce<Partial<Values>>(
      (acc, curr) => {
        return { ...acc, [curr]: "" };
      },
      {}
    );

    return {
      ...values,
      ...updatedValues,
      ...fieldValues,
      email: values.email,
      mobile: values.mobile,
    };
  },
  []);

  const handleCategoryChange: HandleChange = function (values, setValues) {
    return async function (e) {
      setValues(
        resetLaterFields(values, "category", {
          category: e.target.value,
        })
      );
      setSchemeOptions(
        await getNewPurchaseSchemes({
          fund: values.amc,
          schemetype: e.target.value,
        })(dispatch)
      );
    };
  };

  const handleSchemeChange: HandleChange = function (values, setValues) {
    return function (e) {
      const selectedScheme = schemeOptions.find(
        ({ value }) => value === e.target.value
      );
      setValues(
        resetLaterFields(values, "scheme", {
          scheme: e.target.value,
          minAmount: selectedScheme.minAmount,
        })
      );
    };
  };

  const handleComboSwitch: HandleCheckBoxChange = function (values, setValues) {
    return function (e, checked) {
      if (!checked) {
        setValues({ ...values, combo: false });
        return;
      }
      setValues({
        ...values,
        combo: window.confirm(
          "You have opted to do a Purchase cum Switch. Do you want to proceed?"
        ),
      });
    };
  };

  const handleAmcChange: HandleChange = function (values, setValues) {
    return async function (e) {
      try {
        const fund = e.target.value;
        const response = await getNewPurchaseCategory(fund)(dispatch);
        setCategoryOptions(response);
        setValues(
          resetLaterFields(values, "amc", {
            amc: fund,
          })
        );
        const euinResponse = await validateSubBroker({
          arnCode: arnCode,
          fundCode: fund,
          subArnCode: "",
        })(dispatch);
        setEuinOptions(euinResponse);
        if (euinResponse.length === 1) {
          setValues(
            resetLaterFields(values, "amc", {
              amc: fund,
              euin: euinResponse[0].value,
            })
          );
        } else {
          resetLaterFields(values, "amc", {
            amc: fund,
          });
        }
      } catch (error) {
        handleError(error);
        setValues(resetLaterFields(values, "amc", { amc: "" }));
      }
    };
  };

  async function onSubmit(values: Values) {
    try {
      const selectedScheme = schemeOptions.find(
        ({ value }) => values.scheme === value
      );
      const { label, option, plan, scheme } = selectedScheme;
      const requestBody = {
        email: values.email,
        mobile: values.mobile,
        scheme,
        plan,
        option,
        fund: values.amc,
        schemeName: label,
        pan: investorData.PAN,
        investorName: investorData.invname,
        familyEmailFlag: values.emailFamily,
        familyMobileFlag: values.mobileFamily,
        amount: values.amount,
        arnCode: arnCode,
        subArn: values.subArn,
        subBroker: values.subBrokerCode,
        euin: values.euinDeclaration === "Yes" ? values.euin : "",
        euinDeclaration: values.euinDeclaration === "Yes" ? "N" : "Y",
        userId: arnCode,
        category: values.category,
        transactionType: "NPL",
        fundName: funds.find((v) => v.value === values.amc).label,
        combo: values.combo,
        invCategory: values.invCategory,
      };
      history.replace("/new-investor-transaction/investor-details", {
        requestBody: requestBody,
        isCart: false,
        isSingle: true,
        pan: investorData.PAN,
        transactionType: "NPL",
        fund: requestBody.fund,
      });
    } catch (error) {
      handleError(error);
    }
  }

  async function addToCart(
    values: Values,
    {
      validateForm,
      setErrors,
      setFieldTouched,
    }: {
      validateForm: FormikHelpers<Values>["validateForm"];
      setErrors: FormikHelpers<Values>["setErrors"];
      setFieldTouched: FormikHelpers<Values>["setFieldTouched"];
    }
  ) {
    try {
      const errors = await validateForm(values);
      if (Object.keys(errors).length) {
        Object.keys(errors).forEach((error) => {
          setFieldTouched(error);
        });
        setErrors(errors);
        return;
      }
      const selectedScheme = schemeOptions.find(
        ({ value }) => values.scheme === value
      );
      const { label, option, plan, scheme } = selectedScheme;
      const requestBody = {
        email: values.email,
        mobile: values.mobile,
        scheme,
        plan,
        option,
        fund: values.amc,
        schemeName: label,
        pan: investorData.PAN,
        investorName: investorData.invname,
        familyEmailFlag: values.emailFamily,
        familyMobileFlag: values.mobileFamily,
        amount: values.amount,
        arnCode: arnCode,
        subArn: values.subArn,
        subBroker: values.subBrokerCode,
        euin: values.euinDeclaration === "Yes" ? values.euin : "",
        euinDeclaration: values.euinDeclaration === "Yes" ? "N" : "Y",
        invCategory: values.invCategory,
        userId: userId,
        category: values.category,
        fundName: funds.find(({ value }) => value === values.amc).label,
        combo: values.combo,
        transactionType: "NPL",
      };
      const response = await addItemsToCart(requestBody)(dispatch);
      if (response) {
        const [newCartItem] = response.cartItems.sort(
          (item1: any, item2: any) =>
            Number(item2.cartItemId) - Number(item1.cartItemId)
        );
        setModalDetails({
          isOpen: true,
          bulkOrderId: response.bulkOrderId,
          ihNo: newCartItem.ihno,
        });
      }
    } catch (error) {
      handleError(error);
    }
  }
  const handleSubArnChange = function (
    values: Values,
    setValues: React.Dispatch<React.SetStateAction<Values>>
  ): TextFieldProps["onBlur"] {
    return async function (e) {
      try {
        const subArn = e.target.value;
        const response = await validateSubBroker({
          arnCode: values.arnCode,
          subArnCode: `ARN-${subArn}`,
          fundCode: values.amc,
        })(dispatch);
        setEuinOptions(response);
        setValues(resetLaterFields(values, "subArn", { subArn }));
      } catch (error) {
        handleError(error);
      }
    };
  };

  useEffect(() => {
    (async function () {
      try {
        setFunds(await getAmcMasters()(dispatch));
        const kycResponse = await getKycDetails({ pan: investorData.PAN })(
          dispatch
        );
        const DtData = kycResponse?.DtData || [];
        const kycDetails = DtData[0] || {};
        const { email: kycEmail, mobile: kycMobile } = kycDetails;
        setInitialValues((prev) => ({
          ...prev,
          email: kycEmail,
          mobile: kycMobile,
        }));
      } catch (error) {
        handleError(error);
      }

      await getInvestorDetailsByPan(investorData.PAN)(dispatch);
    })();
  }, [dispatch, investorData.PAN]);

  return {
    options: {
      categoryOptions,
      funds,
      schemeOptions,
      family: FAMILY_OPTIONS,
      euinOptions,
    },
    handleCategoryChange,
    handleSchemeChange,
    handleComboSwitch,
    handleAmcChange,
    handleSubArnChange,
    onSubmit,
    addToCart,
    pan: investorData.PAN,
    email,
    mobile,
    arnCode,
    name: investorData.invName,
    initialValues,
    history,
    modalDetails,
  };
}

export const validationSchema = Yup.object().shape({
  amc: Yup.string().required("AMC is required*"),
  category: Yup.string().required("Category is required*"),
  scheme: Yup.string().required("Scheme is required*"),
  amount: Yup.string()
    .required("Investment Amount is required*")
    .test("minAmount", function (value) {
      const { minAmount = 500 } = this.parent;
      if (Number(value) < Number(minAmount)) {
        return this.createError({
          message: `Investment Amount should be greater than or equal to ${convertToInr(
            minAmount
          )}`,
        });
      }
      return true;
    })
    .test("multiples", function (value, { createError, parent }) {
      const { multiples = 1 } = parent;
      if (Number(value) % Number(multiples) !== 0) {
        createError({
          message: `Investment Amount should be a multiple of ${convertToInr(
            multiples
          )}`,
        });
      }
      return true;
    }),
  invCategory: Yup.string().required("Investor Category is required"),
  email: Yup.string().required("Email is required*").email("Invalid Email"),
  emailFamily: Yup.string().required("Please Select Family Email Flag"),
  mobile: Yup.string()
    .required("Mobile is required*")
    .when("invCategory", {
      is: (value: string) => value === "11",
      then: (schema) =>
        schema.matches(mobileRegex, "Enter a valid 10 digit mobile number"),
      otherwise: (schema) =>
        schema.matches(/^[0-9]{5,18}$/, "Enter a valid mobile number"),
    }),
  mobileFamily: Yup.string().required("Please Select Family Mobile Flag"),
  euinDeclaration: Yup.string().required("Euin Declaration is required"),
  euin: Yup.string().when("euinDeclaration", {
    is: "Yes",
    then: (schema) => schema.required("EUIN Number is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  arnCode: Yup.string().required("Arn is required*"),
  subArn: Yup.string()
    .notRequired()
    .test("arnCode", "Invalid ARN Code", function (value) {
      const { arnCode } = this.parent;
      if (arnCode === value || `ARN-${value}` === arnCode) {
        return this.createError({
          message: `ARN Code and Sub ARN Code cannot be same`,
        });
      }

      return true;
    }),
  subBrokerCode: Yup.string()
    // .matches(new RegExp(/^[a-zA-Z0-9]$/), "Enter a valid sub broker code")
    .notRequired(),
});

interface OnChange extends ChangeEvent<{ name?: string; value: any }> {}

type HandleChange = (
  values: Values,
  setValues: React.Dispatch<React.SetStateAction<Values>>
) => (event: OnChange) => any;

interface Values {
  amc: string;
  category: string;
  scheme: string;
  amount: string;
  minAmount?: string | number;
  multiples?: string | number;
  emailFamily: string;
  email: string;
  mobile: string;
  mobileFamily: string;
  euinDeclaration: string;
  euin?: string;
  subArn?: string;
  subBrokerCode?: string;
  combo: boolean;
  arnCode?: string;
  invCategory: "11" | "20";
}

type HandleCheckBoxChange = (
  values: Values,
  setValues: React.Dispatch<React.SetStateAction<Values>>
) => (event: ChangeEvent<HTMLInputElement>, checked: boolean) => any;
